/**
 * Callback — the page Cognito redirects to after successful login.
 *
 * URL shape: /callback?code=AUTHORIZATION_CODE&state=OUR_STATE
 *
 * Flow:
 *   1. Read code + state from query string.
 *   2. Validate state, get the stored code_verifier from sessionStorage.
 *   3. POST {code, code_verifier, redirect_uri} to /api/auth/exchange.
 *      Backend swaps it for tokens and sets HttpOnly cookies.
 *   4. Tell the auth context to re-fetch /me (it'll see the cookie now).
 *   5. Navigate to /ai (or wherever the user was originally headed).
 *
 * Errors at any step bounce the user to /login with a message.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { consumePkceContext } from "../lib/auth";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

export function Callback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const code = searchParams.get("code");
      const receivedState = searchParams.get("state");
      const oauthError = searchParams.get("error");

      // Cognito reports errors via the URL (e.g. ?error=access_denied).
      if (oauthError) {
        setError(`Sign-in error: ${oauthError}`);
        return;
      }

      if (!code) {
        setError("Missing authorization code. Please sign in again.");
        return;
      }

      try {
        const { codeVerifier, redirectUri } = consumePkceContext(receivedState);

        const resp = await fetch(`${API_BASE}/api/auth/exchange`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            code_verifier: codeVerifier,
            redirect_uri: redirectUri,
          }),
        });

        if (!resp.ok) {
          const detail = await resp.text();
          throw new Error(`Token exchange failed (${resp.status}): ${detail}`);
        }

        if (cancelled) return;

        // Refresh auth context so it sees the new cookie / user.
        await refresh();

        if (cancelled) return;

        // Send the user home.
        navigate("/ai", { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, refresh]);

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ background: "var(--bg)" }}
    >
      <div className="text-center max-w-md">
        {error ? (
          <>
            <h1
              className="font-serif text-[20px] mb-3"
              style={{ color: "var(--oxblood)" }}
            >
              Sign-in failed
            </h1>
            <p
              className="font-mono text-[13px] mb-4"
              style={{ color: "var(--ink-2)" }}
            >
              {error}
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="rounded-md py-2 px-4 font-serif text-[14px]"
              style={{
                background: "var(--ink)",
                color: "var(--bg)",
              }}
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <div
            className="font-serif italic text-[14.5px]"
            style={{ color: "var(--ink-3)" }}
          >
            Completing sign-in…
          </div>
        )}
      </div>
    </div>
  );
}